import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { meApi } from '../../api/endpoints/me';
import { useAuth } from '../../hooks/useAuth';
import { handleApiError } from '../../api/errorMapping';
import { SecurityPageSection } from '../layouts/SecurityPageSection';
import { TextField } from '../ui/TextField';
import { LoadingButton } from '../ui/LoadingButton';
import { Button } from '@fiado/react-kit';
import { Banner } from '../ui/Banner';
import {
    EMAIL_OTP_LENGTH,
    OTP_RESEND_COUNTDOWN_SECONDS,
    formatTime,
    maskEmail,
} from '../../lib/otp';

/**
 * Self-service: usuario autenticado vuelve al código por correo (TOTP → EMAIL).
 * Misma experiencia que el OTP de login (`MfaChallengeForm`): se envía el código al entrar,
 * se muestra el correo enmascarado, un relojito de 2 minutos y "reenviar código" (aquí el
 * reenvío SÍ funciona: rehace `request-email-otp`). Al verificar → `change-method(EMAIL_OTP)`.
 */
export function MfaEmailSwitch() {
    const qc = useQueryClient();
    const navigate = useNavigate();
    const { user } = useAuth();
    const email = user?.email ?? '';

    const [code, setCode] = useState('');
    const [countdown, setCountdown] = useState(OTP_RESEND_COUNTDOWN_SECONDS);
    const [sendTick, setSendTick] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const sentRef = useRef(false);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const requestMut = useMutation({
        mutationFn: () => meApi.requestEmailOtp(),
        // Cada envío (inicial o reenvío) reinicia el relojito de 2 min + retrigger del interval.
        onSuccess: () => {
            setCountdown(OTP_RESEND_COUNTDOWN_SECONDS);
            setSendTick((t) => t + 1);
        },
        onError: handleApiError,
    });

    // Auto-enviar el primer OTP al entrar a la pantalla (una sola vez — guard contra el doble
    // invoke de StrictMode).
    useEffect(() => {
        if (sentRef.current) return;
        sentRef.current = true;
        requestMut.mutate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // (Re)arrancar el interval del countdown en cada envío (el reset a 120 lo hace onSuccess del
    // request, no aquí — evita setState directo en el body del effect).
    useEffect(() => {
        intervalRef.current = setInterval(() => {
            setCountdown((prev) => (prev > 0 ? prev - 1 : 0));
        }, 1000);
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [sendTick]);

    const verifyMut = useMutation({
        mutationFn: () => meApi.changeMfaMethod({ newMethod: 'EMAIL_OTP', verificationCode: code }),
        onSuccess: async () => {
            toast.success('Código por correo activado');
            await qc.invalidateQueries({ queryKey: ['me', 'security', 'mfa'] });
            void navigate({ to: '/me/profile' });
        },
        onError: (err) => {
            handleApiError(err);
            setError('Código inválido o expirado. Verifica e intenta de nuevo.');
        },
    });

    const canResend = countdown === 0 && !requestMut.isPending;

    return (
        <SecurityPageSection title="Verifica tu correo" centered>
            <p className="max-w-md text-sm text-muted-foreground">
                {email
                    ? `Te enviamos un código de verificación a ${maskEmail(email)}. Vence en 2 minutos.`
                    : 'Te enviamos un código de verificación a tu correo. Vence en 2 minutos.'}
            </p>
            <div className="max-w-md">
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        setError(null);
                        verifyMut.mutate();
                    }}
                    className="space-y-4"
                    noValidate
                >
                    {error && (
                        <Banner
                            variant="error"
                            dismissible
                            onDismiss={() => {
                                setError(null);
                            }}
                        >
                            {error}
                        </Banner>
                    )}
                    <TextField
                        label="Código del correo"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={EMAIL_OTP_LENGTH}
                        placeholder={'•'.repeat(EMAIL_OTP_LENGTH)}
                        value={code}
                        onChange={(e) => {
                            setCode(e.target.value.replace(/\D/g, ''));
                        }}
                        // eslint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus
                    />
                    <LoadingButton
                        type="submit"
                        className="w-full"
                        isLoading={verifyMut.isPending}
                        loadingText="Verificando…"
                        disabled={code.length !== EMAIL_OTP_LENGTH}
                    >
                        Verificar y activar
                    </LoadingButton>
                </form>

                <div className="mt-6 pt-4 border-t text-center text-sm space-y-2">
                    {countdown > 0 ? (
                        <p className="text-muted-foreground">
                            Puedes pedir un código nuevo en{' '}
                            <span className="font-mono font-semibold">{formatTime(countdown)}</span>
                        </p>
                    ) : (
                        <p className="text-muted-foreground">¿No te llegó el código?</p>
                    )}
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={!canResend}
                        onClick={() => {
                            setError(null);
                            requestMut.mutate();
                        }}
                    >
                        Reenviar código
                    </Button>
                </div>
            </div>
        </SecurityPageSection>
    );
}
